<script lang="ts">
	import classNames from 'classnames';
	import { twMerge } from 'tailwind-merge';
	import { ChevronDownIcon } from '../../../../../assets/icons/ChevronDownIcon';
	import { Link } from '../../../../../components/Link';
	import { Txt, type TxtVariant } from '../../../../../components/Txt';
	import { toKebabCase } from '../../../../../utils/toKebabCase';
	import type { GlobalNavLinkItem } from '../../../types';
	import { browser } from '$app/env';

	interface Props {
		dataCy: string;
		label: string | undefined;
		items: GlobalNavLinkItem[];
		pathname?: string;
		active?: boolean;
		/** Text style of the trigger label; defaults to the site nav's uppercase body text. */
		labelVariant?: TxtVariant;
		labelClass?: string;
		/** Text style of each menu entry, for apps whose nav does not use the site style. */
		itemVariant?: TxtVariant;
		itemClass?: string;
	}

	let {
		label,
		items,
		dataCy,
		pathname = '',
		active = false,
		labelVariant = 'body2',
		labelClass,
		itemVariant = 'body2',
		itemClass
	}: Props = $props();

	let dataCyPrefix = $derived(`${dataCy}-nav-dropdown`);

	let isOpen = $state(false);
	let isVisible = $state(false);
	let dropdownRef = $state<HTMLDivElement>();
	let buttonRef = $state<HTMLButtonElement>();

	const isPathActive = (href?: string) =>
		!!href?.startsWith('/') && (pathname === href || pathname.startsWith(`${href}/`));
	let activeItemHref = $derived(
		items
			.filter((item) => isPathActive(item.href))
			.sort((a, b) => (b.href?.length || 0) - (a.href?.length || 0))[0]?.href
	);

	function handleClick() {
		if (isOpen) {
			closeMenu();
		} else {
			openMenu();
		}
	}

	function handleClickOutside(event: MouseEvent) {
		if (
			dropdownRef &&
			!dropdownRef.contains(event.target as Node) &&
			buttonRef &&
			!buttonRef.contains(event.target as Node)
		) {
			closeMenu();
		}
	}

	function handleEscape(event: KeyboardEvent) {
		if (event.key === 'Escape' && isOpen) {
			closeMenu();
		}
	}

	const openMenu = () => {
		isOpen = true;

		setTimeout(() => {
			isVisible = true;
		}, 0);
	};

	const closeMenu = () => {
		isVisible = false;

		setTimeout(() => {
			isOpen = false;
		}, 200);
	};

	$effect(() => {
		if (!browser) {
			return;
		}

		window.addEventListener('click', handleClickOutside);
		window.addEventListener('keydown', handleEscape);
		window.addEventListener('resize', closeMenu);

		return () => {
			window.removeEventListener('click', handleClickOutside);
			window.removeEventListener('keydown', handleEscape);
			window.removeEventListener('resize', closeMenu);
		};
	});
</script>

<div
	class={classNames(
		'flex h-full cursor-pointer items-center border-b-2 border-transparent py-1 hover:border-b-gray-0',
		active && 'border-b-gray-0'
	)}
>
	<div class="relative h-full">
		<button
			data-cy={dataCyPrefix}
			bind:this={buttonRef}
			onclick={handleClick}
			class="flex gap-1 items-center h-full"
			aria-expanded={isOpen}
			aria-haspopup="true"
			aria-current={active ? 'page' : undefined}
		>
			<Txt
				component="span"
				class={twMerge('text-gray-0 tracking-[.14em] uppercase', labelClass)}
				variant={labelVariant}
			>
				{label}
			</Txt>

			<ChevronDownIcon color="gray-0" class="h-[16px] w-[16px] transition-transform duration-100" />
		</button>

		{#if isOpen}
			<div
				bind:this={dropdownRef}
				class={classNames(
					'absolute right-0 z-50 origin-top-right mt-3 rounded-md bg-gray-900 shadow-lg transition-opacity',
					{
						'opacity-0': !isVisible
					}
				)}
				aria-orientation="vertical"
				aria-labelledby="menu-button"
				role="menu"
				tabindex="0"
			>
				<div class="px-4 py-2">
					{#each items as item}
						{@const itemActive = item.href === activeItemHref}
						<div
							class={classNames('py-1.5', {
								'border-b border-gray-700': item.isLiveDrops,
								'mt-1 border-t border-gray-700 pt-2': item.dividerBefore
							})}
						>
							<div
								class={classNames(
									'flex items-center gap-2 border-l-2 border-transparent px-2 hover:bg-gray-800',
									itemActive && 'border-l-gray-0'
								)}
							>
								{#if item.isLiveDrops}
									<div class="h-[8px] w-[8px] bg-green-300 rounded-full ml-1"></div>
								{/if}
								<Link
									href={item.href}
									data-cy={`${dataCy}-${toKebabCase(label)}-link`}
									class="flex items-center justify-between flex-grow py-2"
									onclick={handleClick}
									aria-current={itemActive ? 'page' : undefined}
									{...item.newTab ? { rel: 'noopener noreferrer', target: '_blank' } : {}}
								>
									<Txt
										variant={itemVariant}
										class={twMerge(
											'text-gray-0 tracking-[.14em] uppercase whitespace-nowrap mr-3',
											itemClass
										)}
									>
										{item.label}
									</Txt>
								</Link>
							</div>
						</div>
					{/each}
				</div>
			</div>
		{/if}
	</div>
</div>
